import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useSEO } from "@/hooks/useSEO";

const features = [
  {
    icon: "ri-smartphone-line",
    title: "MIL-SPEC Rugged Handhelds",
    description:
      "Honeywell CT47 and CK65 mobile computers carry MIL-STD-810H and IP67/IP68 ratings — surviving drops, vibration, water, dust, and extreme temperatures while running enterprise Android applications all day long.",
  },
  {
    icon: "ri-macbook-line",
    title: "Rugged Laptops & Tablets",
    description:
      "Dell Latitude Rugged Extreme laptops and tablets offer MIL-SPEC protection in a portable form factor with hot-swappable batteries, sunlight-readable screens, and optional glove-touch operation for field engineers.",
  },
  {
    icon: "ri-barcode-line",
    title: "Industrial Barcode Scanning",
    description:
      "Honeywell Granit industrial scanners decode damaged, poorly printed, and wet labels at extended ranges — critical for warehouse throughput, receiving operations, and cold-chain logistics tracking.",
  },
  {
    icon: "ri-truck-line",
    title: "Vehicle-Mounted Computing",
    description:
      "Honeywell Thor vehicle computers mount directly to forklifts and fleet vehicles — providing 12-inch touchscreen access to WMS, ERP, and navigation applications without drivers ever leaving the cab.",
  },
  {
    icon: "ri-battery-charge-line",
    title: "Hot-Swap Battery Systems",
    description:
      "Both Honeywell and Dell Rugged devices offer hot-swappable battery configurations, eliminating device downtime during shift changes — a critical capability for 24/7 manufacturing and logistics operations.",
  },
  {
    icon: "ri-settings-line",
    title: "Unified Device Management",
    description:
      "Honeywell Mobility Edge™ and Dell ProManage enable centralized remote management, firmware updates, and security policy enforcement across hundreds of field devices from a single console.",
  },
];

const products = [
  {
    brand: "Dell Rugged",
    relationship: "technology-partner" as const,
    name: "Dell Pro Rugged 10 Tablet",
    spec: "10\" · 1.04kg · MIL-STD-810H · IP66 · Intel Core Ultra 200V · Wi-Fi 7 · 1000-nit",
    image:
      "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/d051876b1c1591957c4ce5f5ac1ec8bd.png",
  },
  {
    brand: "Dell Rugged",
    relationship: "technology-partner" as const,
    name: "Dell Pro Rugged 12 Tablet",
    spec: "12\" · MIL-STD-810H · IP66 · Intel Core Ultra 200V · Wi-Fi 7 · 1200-nit · Dual hot-swap",
    image:
      "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/b44b30507261be801eeddd2018b274b6.png",
  },
  {
    brand: "Dell Rugged",
    relationship: "technology-partner" as const,
    name: "Dell Pro Rugged 13",
    spec: "13\" · MIL-STD-810H · IP65 · 6ft drop · 1400-nit · -20°F to 145°F · optional 5G",
    image:
      "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/2501a5e779d7210cecad3bb678ba7257.png",
  },
  {
    brand: "Dell Rugged",
    relationship: "technology-partner" as const,
    name: "Dell Pro Rugged 14",
    spec: "14\" · MIL-STD-810H · IP53 · 3ft drop · 1100-nit · optional NVIDIA RTX 500 Ada",
    image:
      "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/de10131c1dd492bfb5ee3785ed3b1945.png",
  },
  {
    brand: "Honeywell",
    relationship: "technology-partner" as const,
    name: "ScanPal EDA10A Tablet",
    spec: "10.1\" · Android 12→14 · 5G · Wi-Fi 6 · IP65 · MIL-STD-810H · 8000mAh · 674g",
    image:
      "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/0d8551992a549df2d1d39c5a9bcdb230.jpeg",
  },
  {
    brand: "Honeywell",
    relationship: "technology-partner" as const,
    name: "RT10 Rugged Tablet — Android",
    spec: "10.1\" · Android 9→13 · 4G LTE CAT13 · IP65 · MIL-STD-810G · FlexRange scanner · Hot-swap",
    image:
      "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/ac0c017b0d380fccfdf16d650695a809.jpeg",
  },
  {
    brand: "Honeywell",
    relationship: "technology-partner" as const,
    name: "RT10 Rugged Tablet — Windows",
    spec: "10.1\" · Windows 10 Enterprise LTSC · 4G LTE · IP65 · MIL-STD-810G · FlexRange scanner",
    image:
      "https://static.readdy.ai/image/8ef50732a893e16bbc98824684ca939c/4b6a9df7e8aaf38045343137817e7efb.jpeg",
  },
];

const industries = [
  { icon: "ri-archive-line", name: "Warehousing" },
  { icon: "ri-building-line", name: "Construction" },
  { icon: "ri-truck-line", name: "Logistics & Transport" },
  { icon: "ri-tools-line", name: "Field Services" },
  { icon: "ri-hospital-line", name: "Healthcare" },
  { icon: "ri-government-line", name: "Defence & Military" },
];

const stats = [
  { value: "3x", label: "Longer device lifespan vs consumer" },
  { value: "MIL-810H", label: "Certified Ruggedness Standard" },
  { value: "2", label: "Authorized Rugged Brands" },
  { value: "IP68", label: "Dust & Water Protection Rating" },
];

const ruggedStructuredData = [
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.sedistribution.com.my/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Solutions",
        "item": "https://www.sedistribution.com.my/solutions"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Rugged Computing",
        "item": "https://www.sedistribution.com.my/solutions/rugged-computing"
      }
    ]
  },
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Rugged Computing Solutions Malaysia | Honeywell & Dell MIL-SPEC | SE Distribution",
    "description": "MIL-STD-810H rugged laptops, tablets and handhelds from Honeywell and Dell Rugged Pro. IP66/IP68 rated, hot-swappable batteries, 3x longer lifespan vs consumer devices. Authorized distributor in Kuala Lumpur, Malaysia.",
    "url": "https://www.sedistribution.com.my/solutions/rugged-computing",
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.sedistribution.com.my/" },
        { "@type": "ListItem", "position": 2, "name": "Solutions", "item": "https://www.sedistribution.com.my/solutions" },
        { "@type": "ListItem", "position": 3, "name": "Rugged Computing", "item": "https://www.sedistribution.com.my/solutions/rugged-computing" }
      ]
    },
    "publisher": {
      "@type": "Organization",
      "name": "SE Distribution",
      "url": "https://www.sedistribution.com.my"
    }
  }
];

export default function RuggedComputingPage() {
  useSEO({
    title: "Rugged Computing Solutions Malaysia | Honeywell & Dell MIL-SPEC Tablets | SE Distribution",
    description: "MIL-STD-810H rugged laptops, tablets & handhelds from Honeywell and Dell Rugged Pro. IP66 rated, hot-swap batteries, 3x longer lifespan. Authorized distributor in Kuala Lumpur, Malaysia.",
    canonical: "https://www.sedistribution.com.my/solutions/rugged-computing",
    keywords: "rugged tablet Malaysia, MIL-SPEC laptop Malaysia, Honeywell rugged tablet, Dell rugged laptop, rugged computing Malaysia, warehouse tablet, field device Malaysia",
    structuredData: ruggedStructuredData,
  });
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative w-full h-[480px] md:h-[580px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=construction%20site%20field%20engineer%20using%20rugged%20laptop%20tablet%20outdoor%20bright%20daylight%20heavy%20equipment%20machinery%20background%20professional%20worker%2C%20dramatic%20cinematic%20lighting%2C%20wide%20angle&width=1400&height=700&seq=rugged-hero&orientation=landscape"
          alt="Rugged Computing Solutions"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/50 to-black/20"></div>
        <div className="absolute inset-0 flex flex-col justify-center px-6 lg:px-20 max-w-7xl mx-auto w-full">
          <nav className="flex items-center gap-2 text-xs text-white/50 mb-6">
            <Link to="/" className="hover:text-white/80 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/solutions" className="hover:text-white/80 transition-colors">Solutions</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white/80">Rugged Computing</span>
          </nav>
          <span className="text-xs font-semibold uppercase tracking-widest text-white/50 mb-3">Solutions</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-4 max-w-2xl">
            Rugged Computing Solutions
          </h1>
          <p className="text-lg text-white/75 max-w-xl leading-relaxed mb-8">
            MIL-SPEC laptops, tablets, and handheld computers from Honeywell and Dell Rugged Pro — built for the harshest field environments.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/contact"
              className="bg-white text-[#1a1a1a] text-sm font-semibold px-7 py-3.5 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2 w-fit"
            >
              Request a Demo <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/brands"
              className="border border-white/50 text-white text-sm font-medium px-7 py-3.5 rounded-md hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer w-fit"
            >
              View Brand Profiles
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#1a1a1a] py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-20 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <p className="text-2xl md:text-3xl font-bold text-white mb-1">{s.value}</p>
              <p className="text-xs text-white/50 uppercase tracking-wider">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Rugged Spotlight Banner */}
      <section className="py-10 px-6 lg:px-20 bg-[#f0ede8]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-8 bg-white rounded-xl p-8 border border-[#1a1a1a]/10">
            <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center bg-[#1a1a1a] rounded-xl">
              <i className="ri-smartphone-line text-white text-2xl"></i>
            </div>
            <div className="flex-1 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-[#1a1a1a]/5 rounded-full px-3 py-1 mb-2">
                <i className="ri-star-fill text-[#1a1a1a] text-xs"></i>
                <span className="text-xs font-semibold text-[#1a1a1a] uppercase tracking-widest">Featured Spotlight</span>
              </div>
              <h3 className="text-xl font-bold text-[#1a1a1a] mb-1">Honeywell &amp; Dell Rugged — Full Device Lineup &amp; Specs</h3>
              <p className="text-sm text-gray-500 leading-relaxed max-w-2xl">
                Explore the complete Honeywell and Dell Rugged Pro device range — from the CT47 5G handheld to the Dell Latitude 7330 Rugged Extreme laptop. Compare specs, features, and find the right device for your field operations.
              </p>
            </div>
            <Link
              to="/rugged-spotlight"
              className="flex-shrink-0 bg-[#1a1a1a] text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
            >
              View Rugged Spotlight <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 px-6 lg:px-20 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">Overview</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] leading-tight mb-5">
              Devices That Work as Hard as Your Teams
            </h2>
            <p className="text-gray-600 text-base leading-relaxed mb-4">
              Consumer devices simply are not built for job sites, warehouses, or field operations. SE Distribution partners with Honeywell and Dell Rugged Pro to provide purpose-built computing hardware that withstands drops, water ingress, extreme temperatures, and constant vibration — while running enterprise software reliably across every shift.
            </p>
            <p className="text-gray-600 text-base leading-relaxed mb-8">
              From handheld barcode scanners on the warehouse floor to fully rugged laptops in military deployments, we match the right device to your specific workflow — reducing downtime, improving data accuracy, and extending asset life far beyond what standard hardware can deliver.
            </p>
            <div className="flex flex-wrap gap-3">
              {["Honeywell", "Dell Rugged Pro"].map((b) => (
                <span key={b} className="text-xs font-semibold bg-[#f5f3ee] text-[#1a1a1a] px-4 py-1.5 rounded-full">
                  {b}
                </span>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="w-full h-80 rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=warehouse%20worker%20scanning%20barcode%20handheld%20rugged%20device%20picking%20inventory%20industrial%20environment%2C%20dramatic%20lighting%2C%20realistic%20photography%2C%20high%20detail%2C%20logistics%20operations&width=700&height=500&seq=rugged-overview&orientation=landscape"
                alt="Field worker with rugged device"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-white rounded-lg px-5 py-4 border border-gray-100 w-48">
              <p className="text-xs text-gray-400 mb-1">Authorized Brands</p>
              <span className="text-sm font-bold text-[#1a1a1a]">Honeywell · Dell Rugged Pro</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="bg-[#f8f7f4] py-20 px-6 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">Capabilities</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a]">What Our Solutions Deliver</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f) => (
              <div key={f.title} className="bg-white rounded-lg p-7 border border-transparent hover:border-[#1a1a1a]/10 transition-colors">
                <div className="w-11 h-11 flex items-center justify-center bg-[#1a1a1a]/5 rounded-lg mb-4">
                  <i className={`${f.icon} text-xl text-[#1a1a1a]`}></i>
                </div>
                <h3 className="font-bold text-[#1a1a1a] text-base mb-2">{f.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="py-20 px-6 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">Product Highlights</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a]">Featured Rugged Devices</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p) => (
              <div key={p.name} className="rounded-lg overflow-hidden bg-[#f8f7f4] group cursor-pointer">
                <div className="w-full h-56 bg-white flex items-center justify-center overflow-hidden p-4">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{p.brand}</span>
                    {p.relationship === "distributor" ? (
                      <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider bg-[#1a1a1a] text-white px-1.5 py-0.5 rounded-full whitespace-nowrap">
                        <i className="ri-verified-badge-line"></i> Authorized
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider bg-[#f5f3ee] text-gray-600 border border-gray-200 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                        <i className="ri-shake-hands-line"></i> Tech Partner
                      </span>
                    )}
                  </div>
                  <h3 className="font-bold text-[#1a1a1a] text-base mb-2">{p.name}</h3>
                  <p className="text-xs text-gray-500">{p.spec}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="bg-[#1a1a1a] py-16 px-6 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-3 block">Industries Served</span>
            <h2 className="text-2xl md:text-3xl font-bold text-white">Who We Serve</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
            {industries.map((ind) => (
              <div key={ind.name} className="flex flex-col items-center gap-2 text-center py-5 px-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors cursor-pointer">
                <div className="w-10 h-10 flex items-center justify-center">
                  <i className={`${ind.icon} text-2xl text-white/70`}></i>
                </div>
                <p className="text-xs text-white/60">{ind.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 lg:px-20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">
            Find the Right Rugged Device for Your Team
          </h2>
          <p className="text-gray-500 text-base mb-8 max-w-xl mx-auto">
            Our solutions team will match the right hardware to your workflow, environment, and budget — with full deployment and after-sales support.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-[#1a1a1a] text-white text-sm font-semibold px-8 py-3.5 rounded-md hover:bg-[#333] transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-2 justify-center"
            >
              Talk With Us <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/brands"
              className="border border-[#1a1a1a] text-[#1a1a1a] text-sm font-medium px-8 py-3.5 rounded-md hover:bg-[#f8f7f4] transition-colors whitespace-nowrap cursor-pointer"
            >
              View All Brands
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
